import React, { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ClipboardList, RefreshCw, ShieldCheck, ShieldX, KeyRound, Trash2, Play, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function actionMeta(action) {
  if (action.startsWith("verification.approve")) return { icon: <ShieldCheck className="w-3.5 h-3.5" />, color: "text-emerald-300" };
  if (action.startsWith("verification.reject"))  return { icon: <ShieldX className="w-3.5 h-3.5" />, color: "text-red-300" };
  if (action.startsWith("reset."))                return { icon: <KeyRound className="w-3.5 h-3.5" />, color: "text-amber-300" };
  return { icon: <ClipboardList className="w-3.5 h-3.5" />, color: "text-zinc-300" };
}

export default function AdminAuditLog() {
  const [q, setQ] = useState("");
  const qd = useDebounced(q, 300);
  const [action, setAction] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cleanup, setCleanup] = useState(null);   // { last: {...}, history: [...] }
  const [runningCleanup, setRunningCleanup] = useState(false);

  const fetchCleanup = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cleanup/status");
      setCleanup(data || null);
    } catch (_) { /* non-critical */ }
  }, []);

  const triggerCleanup = async () => {
    setRunningCleanup(true);
    try {
      const { data } = await api.post("/admin/cleanup/run");
      toast.success(`Cleanup ran: ${data.verification_codes_removed} codes, ${data.password_resets_removed} resets removed (${data.duration_ms}ms)`);
      await fetchCleanup();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cleanup failed");
    } finally { setRunningCleanup(false); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/manual-verify/audit", {
        params: { q: qd, action, limit: 200 },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load audit log");
    } finally { setLoading(false); }
  }, [qd, action]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchCleanup(); }, [fetchCleanup]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mystic text-rose-400 text-xs">記録</div>
          <h2 className="font-display text-2xl font-bold text-white">Audit Log</h2>
          <p className="text-zinc-400 text-xs mt-1">All manual verification &amp; password-reset actions.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="border-white/15 text-zinc-300" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
      <div className="rounded-xl border border-white/10 bg-zinc-950/40 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="cleanup-panel">
        <div className="flex items-start gap-3 min-w-0">
          <Trash2 className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-zinc-100 text-sm font-semibold">Automatic Cleanup</div>
            <div className="text-zinc-400 text-xs mt-0.5">
              Runs every 24h. Removes expired manual verification codes (&gt;7d, unverified users) and expired password-reset requests.
              Verified users &amp; audit log are never affected.
            </div>
            {cleanup?.last ? (
              <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Last run: {new Date(cleanup.last.executed_at).toLocaleString()}</span>
                <span className="text-zinc-400">Verification codes removed: <b className="text-rose-300">{cleanup.last.verification_codes_removed}</b></span>
                <span className="text-zinc-400">Reset requests removed: <b className="text-amber-300">{cleanup.last.password_resets_removed}</b></span>
                <span className="text-zinc-600">({cleanup.last.duration_ms} ms)</span>
              </div>
            ) : (
              <div className="text-xs text-zinc-500 mt-2 italic">No cleanup runs recorded yet.</div>
            )}
          </div>
        </div>
        <Button
          size="sm"
          onClick={triggerCleanup}
          disabled={runningCleanup}
          className="bg-rose-600/80 hover:bg-rose-500 text-white"
          data-testid="cleanup-run-btn"
        >
          <Play className="w-3.5 h-3.5 mr-1.5" />{runningCleanup ? "Running…" : "Run Now"}
        </Button>
      </div>

        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by code, email, or admin name…"
            className="pl-10 bg-black/40 border-white/10 text-white"
            data-testid="audit-search"
          />
        </div>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-zinc-200 text-sm"
          data-testid="audit-action"
        >
          <option value="all">All actions</option>
          <option value="verification">Verification (all)</option>
          <option value="verification.approve">Verification — approve</option>
          <option value="verification.reject">Verification — reject</option>
          <option value="reset">Reset (all)</option>
          <option value="reset.approve">Reset — approve</option>
          <option value="reset.reject">Reset — reject</option>
          <option value="reset.set_temporary">Reset — set temporary</option>
          <option value="reset.force_change">Reset — force change</option>
        </select>
        <span className="text-xs text-zinc-500">{total} entr{total === 1 ? "y" : "ies"}</span>
      </div>

      <div className="glass rounded-xl border border-white/10 overflow-hidden">
        {loading && (
          <div className="px-4 py-3 text-xs text-zinc-500 italic">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-zinc-400">No audit entries.</div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-black/40 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Action</th>
                <th className="text-left px-4 py-2">Admin</th>
                <th className="text-left px-4 py-2">Target user</th>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const m = actionMeta(r.action);
                return (
                  <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-xs text-zinc-400 whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}</td>
                    <td className={`px-4 py-3 text-xs font-mono ${m.color}`}>
                      <span className="inline-flex items-center gap-1">{m.icon}{r.action}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-200">{r.admin_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-300">{r.target_user_email || r.target_user_id || "—"}</td>
                    <td className="px-4 py-3"><code className="bg-black/40 px-1.5 py-0.5 rounded text-rose-300 font-mono text-xs">{r.code || "—"}</code></td>
                    <td className="px-4 py-3 text-xs text-zinc-400 max-w-[260px] truncate">{r.payload?.admin_note || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
